<template>
  <div>
    <b-card>
      <h3 class="text-center mb-0">
        {{ $t('multi_poster.title') }}
      </h3>
      <p class="text-center text-muted mb-0 mt-1">
        {{ $t('multi_poster.subtitle') }}
      </p>
    </b-card>

    <b-row>
      <b-col
        md="8"
        sm="12"
      >
        <b-card>
          <error-alert
            v-if="error"
            :error="error"
          />

          <h5 class="font-weight-bold">
            {{ $t('multi_poster.targets') }}
          </h5>
          <b-row>
            <b-col
              md="4"
              sm="12"
              class="mb-1"
            >
              <label>{{ $t('multi_poster.facebook') }}</label>
              <b-form-select
                v-model="form.page_id"
                :options="pageOptions"
              />
            </b-col>
            <b-col
              md="4"
              sm="12"
              class="mb-1"
            >
              <label>{{ $t('multi_poster.instagram') }}</label>
              <b-form-select
                v-model="form.account_id"
                :options="accountOptions"
              />
            </b-col>
            <b-col
              md="4"
              sm="12"
              class="mb-1"
            >
              <label>{{ $t('multi_poster.telegram') }}</label>
              <div class="d-flex">
                <b-form-select
                  v-model="form.telegram_destination_id"
                  :options="telegramOptions"
                  class="flex-grow-1"
                />
              </div>
              <div class="mt-1 d-flex flex-wrap">
                <b-button
                  size="sm"
                  variant="outline-primary"
                  class="mr-1 mb-1"
                  :disabled="refreshingTg"
                  @click="refreshTelegram"
                >
                  {{ $t('multi_poster.detect_telegram') }}
                </b-button>
                <b-button
                  size="sm"
                  variant="outline-secondary"
                  class="mb-1"
                  @click="showVerify = !showVerify"
                >
                  {{ $t('multi_poster.add_chat') }}
                </b-button>
              </div>
              <div
                v-if="showVerify"
                class="mt-1"
              >
                <ok-text-input
                  v-model="verifyChat"
                  :label="$t('multi_poster.chat_hint')"
                  :placeholder="'@channel or -100...'"
                />
                <b-button
                  size="sm"
                  variant="success"
                  :disabled="verifyingTg"
                  @click="verifyTelegram"
                >
                  {{ $t('forms.submit') }}
                </b-button>
              </div>
              <small class="text-muted d-block mt-1">
                {{ $t('multi_poster.telegram_detect_hint') }}
                <router-link :to="{ name: 'owner:telegram' }">
                  {{ $t('multi_poster.configure_bot') }}
                </router-link>
              </small>
            </b-col>
          </b-row>

          <hr>

          <h5 class="font-weight-bold">
            {{ $t('poster.message') }}
          </h5>
          <textarea
            v-model="form.message"
            rows="5"
            class="form-control mb-2"
            :placeholder="$t('poster.status_placeholder')"
          />

          <h5 class="font-weight-bold">
            {{ $t('poster.image_title') }}
          </h5>
          <my-menu-media-input
            v-model="form.files"
            :max-files="10"
          />

          <hr>

          <div v-if="form.page_id || form.account_id">
            <h5 class="font-weight-bold">
              {{ $t('poster.reply-title') }}
            </h5>
            <b-form-checkbox
              v-model="addReply"
              switch
              class="mb-1"
            >
              {{ $t('poster.choose_reply_title') }}
            </b-form-checkbox>
            <ok-text-input
              v-if="addReply"
              v-model="form.reply_message"
              textarea
              :label="$t('poster.reply-message-title')"
              :placeholder="$t('poster.reply-message-hint')"
            />
            <hr>
          </div>

          <h5 class="font-weight-bold">
            {{ $t('poster.schedule_time_title') }}
          </h5>
          <div class="demo-inline-spacing mb-2">
            <b-form-radio
              v-model="form.schedule_type"
              name="multi_schedule"
              value="now"
            >
              {{ $t('poster.publish_post') }}
            </b-form-radio>
            <b-form-radio
              v-model="form.schedule_type"
              name="multi_schedule"
              value="later"
            >
              {{ $t('poster.schedule_post') }}
            </b-form-radio>
          </div>
          <ok-date-time-picker
            v-if="form.schedule_type === 'later'"
            v-model="form.schedule_time"
            type="datetime"
            :label="$t('poster.schedule_time')"
            is-required
          />

          <b-button
            variant="success"
            class="btn-block mt-2"
            :disabled="loading"
            @click="submit"
          >
            <b-spinner
              v-if="loading"
              small
              class="mr-1"
            />
            {{ $t('forms.submit') }}
          </b-button>
        </b-card>
      </b-col>

      <b-col
        md="4"
        sm="12"
      >
        <b-card>
          <h5 class="font-weight-bold">
            {{ $t('multi_poster.history') }}
          </h5>
          <fetching-data-spinner
            :loading="historyLoading"
            :error="historyError"
            enable-retry
            @retry="loadHistory"
          />
          <div
            v-for="post in history"
            :key="post.id"
            class="border rounded p-1 mb-1"
          >
            <div class="small text-muted">
              #{{ post.id }} · {{ post.created_at }}
            </div>
            <div class="mb-1">
              {{ (post.message || '').slice(0, 80) }}
            </div>
            <div>
              <b-badge
                v-for="t in (post.targets || [])"
                :key="t.id"
                :variant="targetVariant(t.status)"
                class="mr-50 mb-50"
              >
                {{ t.platform }}: {{ targetStatus(t.status) }}
              </b-badge>
            </div>
          </div>
          <p
            v-if="!historyLoading && !history.length"
            class="text-muted mb-0"
          >
            {{ $t('multi_poster.no_history') }}
          </p>
        </b-card>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import {
  BCard, BRow, BCol, BFormSelect, BButton, BFormCheckbox, BFormRadio, BSpinner, BBadge,
} from 'bootstrap-vue'
import { mapGetters } from 'vuex'
import ErrorAlert from '@/@bya3/components/alerts/ErrorAlert'
import OkTextInput from '@/@bya3/components/inputs/OkTextInput'
import OkDateTimePicker from '@/@bya3/components/inputs/OkDateTimePicker'
import MyMenuMediaInput from '@/views/media/MyMenuMediaInput'
import FetchingDataSpinner from '@/@bya3/components/utils/FetchingDataSpinner'
import MultiPosterApi from '@/api/multi-poster'
import UserTelegramApi from '@/api/user/telegram'

export default {
  name: 'MultiPosterPage',
  components: {
    BCard,
    BRow,
    BCol,
    BFormSelect,
    BButton,
    BFormCheckbox,
    BFormRadio,
    BSpinner,
    BBadge,
    ErrorAlert,
    OkTextInput,
    OkDateTimePicker,
    MyMenuMediaInput,
    FetchingDataSpinner,
  },
  data() {
    return {
      error: null,
      loading: false,
      addReply: false,
      showVerify: false,
      verifyChat: '',
      refreshingTg: false,
      verifyingTg: false,
      telegramDestinations: [],
      history: [],
      historyLoading: false,
      historyError: null,
      form: {
        page_id: null,
        account_id: null,
        telegram_destination_id: null,
        message: '',
        files: [],
        reply_message: '',
        schedule_type: 'now',
        schedule_time: '',
      },
    }
  },
  computed: {
    ...mapGetters({
      pages: 'pages/pages',
      currentPageId: 'pages/currentPageId',
      accounts: 'instagram/accounts',
      currentAccountId: 'instagram/currentAccountId',
    }),
    pageOptions() {
      const opts = [{ value: null, text: this.$t('multi_poster.none') }]
      const pages = this.pages || []
      pages.filter(p => !p.expired).forEach(p => {
        opts.push({ value: p.id, text: p.name })
      })
      return opts
    },
    accountOptions() {
      const opts = [{ value: null, text: this.$t('multi_poster.none') }]
      const accounts = this.accounts || []
      accounts.filter(a => !a.expired).forEach(a => {
        opts.push({ value: a.id, text: a.username || a.name || `#${a.id}` })
      })
      return opts
    },
    telegramOptions() {
      const opts = [{ value: null, text: this.$t('multi_poster.none') }]
      const destinations = this.telegramDestinations || []
      destinations.forEach(d => {
        opts.push({ value: d.id, text: d.title || d.username || d.chat_id })
      })
      return opts
    },
  },
  async mounted() {
    this.form.page_id = this.currentPageId || null
    this.form.account_id = this.currentAccountId || null
    await Promise.all([this.loadTelegram(), this.loadHistory()])
  },
  methods: {
    targetVariant(status) {
      if (status === 1) return 'success'
      if (status === 2) return 'danger'
      return 'warning'
    },
    targetStatus(status) {
      if (status === 1) return this.$t('multi_poster.status_ok')
      if (status === 2) return this.$t('multi_poster.status_fail')
      return this.$t('multi_poster.status_pending')
    },
    async loadTelegram() {
      try {
        const res = await UserTelegramApi.destinations()
        this.telegramDestinations = res.destinations || []
      } catch (e) {
        // optional until telegram linked
      }
    },
    async refreshTelegram() {
      this.refreshingTg = true
      try {
        const res = await UserTelegramApi.refreshDestinations()
        this.telegramDestinations = res.destinations || []
        this.$notifySuccess(res.message || this.$t('multi_poster.detect_done'))
      } catch (e) {
        this.$notifyError(e)
      } finally {
        this.refreshingTg = false
      }
    },
    async verifyTelegram() {
      this.verifyingTg = true
      try {
        const res = await UserTelegramApi.verifyDestination(this.verifyChat)
        if (res.error === 1) {
          this.$notifyError(res.message)
        } else {
          this.$notifySuccess(res.message)
          this.verifyChat = ''
          this.showVerify = false
          await this.loadTelegram()
          if (res.destination?.id) this.form.telegram_destination_id = res.destination.id
        }
      } catch (e) {
        this.$notifyError(e)
      } finally {
        this.verifyingTg = false
      }
    },
    async loadHistory() {
      this.historyLoading = true
      this.historyError = null
      try {
        const res = await MultiPosterApi.list({ per_page: 10 })
        const posts = res.posts?.data || res.posts || []
        this.history = posts
      } catch (e) {
        this.historyError = e
      } finally {
        this.historyLoading = false
      }
    },
    async submit() {
      this.error = null
      if (!this.form.page_id && !this.form.account_id && !this.form.telegram_destination_id) {
        this.error = this.$t('multi_poster.need_target')
        return
      }
      if (this.form.account_id && !(this.form.files || []).length) {
        this.error = this.$t('multi_poster.ig_needs_media')
        return
      }
      if (this.form.schedule_type === 'later' && !this.form.schedule_time) {
        this.error = this.$t('multi_poster.need_schedule')
        return
      }
      this.loading = true
      try {
        const payload = {
          page_id: this.form.page_id || null,
          account_id: this.form.account_id || null,
          telegram_destination_id: this.form.telegram_destination_id || null,
          message: this.form.message,
          files: this.form.files,
          schedule_type: this.form.schedule_type,
          schedule_time: this.form.schedule_time || null,
          reply_message: this.addReply ? this.form.reply_message : null,
        }
        const res = await MultiPosterApi.process(payload)
        if (res.error === 1) {
          this.error = res.message
        } else {
          this.$notifySuccess(res.message)
          this.form.message = ''
          this.form.files = []
          this.form.reply_message = ''
          this.addReply = false
          await this.loadHistory()
        }
      } catch (e) {
        this.error = e
      } finally {
        this.loading = false
      }
    },
  },
}
</script>
